import * as logger from 'universal-logger';
import { fetchEntityIfNeeded, fetchEntity } from '@classpass/saga-tools';
import { delay, call, takeEvery, takeLatest, put, select, spawn } from 'redux-saga/effects';
import { push as updatePath } from 'react-router-redux';
import { keywordAutocomplete, searchLayoutByName, searchLocationAutocomplete } from 'redux/saga/entities/bound';
import { getUserId } from 'redux/saga/helpers';
import { userId as userIdSelector } from 'redux/saga/selectors/user';
import { isBlank } from 'helpers/verify';
import { filterSetFilters, pathSelector } from 'redux/saga/selectors';
import { browserHistory } from 'react-router';
import { getVerticalOptions, searchFilterActions } from 'features/Search/filters/filters.helpers';
import { VENUE_DETAIL_ROUTE } from 'features/InventoryDetail/InventoryDetail.route.path';
import { FILTER_NAMES, HEADER_LAYOUT } from 'features/Search/Search.constants';
import { ec } from 'helpers/multitrack';
import { segment } from 'helpers/segment';
import {
  selectActiveLocation,
  selectOpenPanel,
  setLocationKey,
  setKeyword,
  setKeywordKey,
  SET_OPEN_PANEL,
  SET_KEYWORD_VALUE,
  SET_LOCATION_VALUE,
  setSelectedLocation,
} from './SearchBox.reducer';
import { EVENTS, ITEM_TYPES, PANEL_NAMES } from './SearchBox.constants';
import {
  getUserLocationForSearch,
  selectDataFromResultsLayout,
  selectKeywordAutocompleteValueInLayout,
} from '../Search.helpers';
import { SEARCH_PATH } from '../Search.route.path';
import { SearchLocation } from '../models/SearchLocation';

const { addFilter, clearFilter, setFilterValue } = searchFilterActions;

const SEARCH_PANEL_ITEM_SELECTED = 'SEARCH_PANEL_ITEM_SELECTED';
const SEARCH_PANEL_REMOVE_HISTORY_ITEM = 'SEARCH_PANEL_REMOVE_HISTORY_ITEM';

export const searchPanelItemSelected = (panel, item) => ({ type: SEARCH_PANEL_ITEM_SELECTED, panel, item });
export const removeSearchboxHistoryItem = (itemType, itemId) => ({
  type: SEARCH_PANEL_REMOVE_HISTORY_ITEM,
  itemType,
  itemId,
});

export function* doTrackSearchBoxSelected() {
  const openPanel = yield select(selectOpenPanel);

  if (openPanel === PANEL_NAMES.location) {
    yield call(segment.track, EVENTS.clickLocationBox);
    yield call(ec, EVENTS.clickLocationBox);
  } else if (openPanel === PANEL_NAMES.keyword) {
    yield call(segment.track, EVENTS.clickKeywordBox);
    yield call(ec, EVENTS.clickKeywordBox);
  }
}

export function* doLoadPanelData(client, force = false) {
  const { lat, lon } = yield select(getUserLocationForSearch);
  const method = force ? fetchEntity : fetchEntityIfNeeded;

  return yield call(method, searchLayoutByName, [
    client,
    HEADER_LAYOUT,
    {
      header_request: { location: { lat, lon } },
    },
  ]);
}

export function* doTrackSearchPanelItemSelection(panel, item) {
  if (item) {
    if (panel === PANEL_NAMES.keyword) {
      const keywordBoxText =
        item.type === ITEM_TYPES.venue && item.subtitle ? `${item.name} - ${item.subtitle}` : item.name;
      yield call(segment.track, EVENTS.selectKeyword, {
        keyword_box_text: keywordBoxText,
        keyword_id: item.id,
        header_type: item.type,
      });
      yield call(ec, EVENTS.selectKeyword, {
        keyword_box_text: keywordBoxText,
        keyword_id: item.id,
        header_type: item.type,
      });
    } else if (panel === PANEL_NAMES.location) {
      yield call(segment.track, EVENTS.selectLocation, {
        location_box_text: item.main_text,
        header_type: item.type,
      });
      yield call(ec, EVENTS.selectLocation, {
        location_box_text: item.main_text,
        header_type: item.type,
      });
    }
  }
}

export function* setVertical(vertical) {
  const searchFilter = yield select(filterSetFilters);
  // we check if the filters have been created before adding the new filter
  if (!searchFilter) {
    yield put(addFilter(FILTER_NAMES.VERTICAL, getVerticalOptions(), null, { exclusive: true }));
  }
  yield put(
    setFilterValue(FILTER_NAMES.VERTICAL, vertical, {
      excludeFromWriteActions: true,
    })
  );
}

export function* setActivity(item) {
  if (item.vertical) {
    yield call(setVertical, item.vertical);
  }
  yield put(setKeyword(item));
}

export function* doSearchPanelItemSelected(client, { panel, item: panelItem }) {
  const item = { ...panelItem };
  if (panel === PANEL_NAMES.keyword) {
    if (!item) {
      console.log('Got request to clear keyword'); // eslint-disable-line no-console
    } else if (item.type === ITEM_TYPES.venue) {
      yield call(window.open, `${VENUE_DETAIL_ROUTE}/${item.alias}`, '_blank');
    } else if (item.type === ITEM_TYPES.activity) {
      yield call(setActivity, item);
    } else if (item.type === ITEM_TYPES.links && item.to) {
      yield call(browserHistory.push, item.to);
    } else if (item.type === ITEM_TYPES.bookmarks) {
      yield put(setKeyword(item));
    }
  } else if (panel === PANEL_NAMES.location) {
    if (!item) {
      console.log('Got request to clear location'); // eslint-disable-line no-console
    } else {
      if (!item.lat || !item.lon || !item.calculated_radius_km || !item.calculated_radius_miles) {
        const { data: placeData } = yield call(client.search.location.details, item.place_id);
        item.lat = placeData.lat;
        item.lon = placeData.lon;
        item.timezone = placeData.timezone;
        item.calculated_radius_miles = placeData.estimated_radius_miles || placeData.radius;
        item.calculated_radius_km = placeData.estimated_radius_km || placeData.radius_km;
      }

      yield put(setSelectedLocation(new SearchLocation(item)));
      yield put(clearFilter(FILTER_NAMES.RADIUS));

      const userId = yield call(getUserId, client);
      if (userId) {
        yield spawn(client.search.location.saveForUser, userId, item.place_id);
      }
    }
  }

  // Bust the cache for the web header layout
  yield put(searchLayoutByName.resetAction({ key: HEADER_LAYOUT }));

  // If this action came while we were not on the search page, do a search and get over there
  if (item && item.type !== ITEM_TYPES.venue && item.type !== ITEM_TYPES.links) {
    const currentPath = yield select(pathSelector);
    if (currentPath.indexOf(SEARCH_PATH) !== 0) {
      yield put(updatePath(SEARCH_PATH));
    }
  }

  yield spawn(doTrackSearchPanelItemSelection, panel, item);
}

export function* doSearchPanelRemoveHistoryItem(client, { itemType, itemId }) {
  const userId = yield select(userIdSelector);
  try {
    if (itemType === ITEM_TYPES.location) {
      yield call(client.search.location.removeHistoryItem, userId, itemId);
    } else {
      yield call(client.search.keyword.removeHistoryItem, userId, itemId);
    }
  } catch (err) {
    logger.error('Failed while removing search box history item', err);
  }

  yield call(doLoadPanelData, client, true);
}

export function* doAutocomplete(panel, client, { value }) {
  try {
    const openPanel = yield select(selectOpenPanel);
    if (openPanel === panel && !isBlank(value)) {
      // This delay works alongside `takeLatest` to become a debounce for requests
      yield delay(250);
      const { lat, lon } = yield select(selectActiveLocation);
      if (panel === PANEL_NAMES.keyword) {
        yield call(
          fetchEntityIfNeeded,
          keywordAutocomplete,
          [
            client,
            value,
            {
              lat,
              lon,
            },
          ],
          { key: value }
        );
        yield put(setKeywordKey(value));
      } else if (panel === PANEL_NAMES.location) {
        yield call(
          fetchEntity,
          searchLocationAutocomplete,
          [
            client,
            {
              query: value,
              near: {
                lat,
                lon,
              },
            },
          ],
          { key: value }
        );
        yield put(setLocationKey(value));
      }
    }
  } catch (err) {
    logger.error('Failed while fetching autocomplete data', err);
  }
}

export function* setActivityKeyword(activity, options) {
  const resultsLayoutData = yield select(selectDataFromResultsLayout);
  const keywordName = selectKeywordAutocompleteValueInLayout(resultsLayoutData);

  yield put(
    setKeyword(
      {
        id: activity,
        name: keywordName,
      },
      options
    )
  );
}

export function* watchSearchBoxSagas(client) {
  yield takeEvery(SEARCH_PANEL_ITEM_SELECTED, doSearchPanelItemSelected, client);
  yield takeEvery(SEARCH_PANEL_REMOVE_HISTORY_ITEM, doSearchPanelRemoveHistoryItem, client);
  yield takeEvery(SET_OPEN_PANEL, doLoadPanelData, client, false);
  yield takeEvery(SET_OPEN_PANEL, doTrackSearchBoxSelected);
  yield takeLatest(SET_KEYWORD_VALUE, doAutocomplete, PANEL_NAMES.keyword, client);
  yield takeLatest(SET_LOCATION_VALUE, doAutocomplete, PANEL_NAMES.location, client);
}
